/**
 * 类型化 API 端点封装
 * 注意：response 拦截器已解包信封，故此处返回值即 envelope.data
 */
import client from './api';
import type {
  Agent,
  AgentAccess,
  AgentCategory,
  AgentListQuery,
  AgentSummary,
  ConversationDetail,
  ConversationListQuery,
  ConversationListResult,
  CreateTaskRequest,
  CreateTaskResult,
  LoginRequest,
  Order,
  Plan,
  PrepayResult,
  QuotaInfo,
  ReferralInfo,
  ReferralRecord,
  RegisterRequest,
  TaskView,
  Tokens,
  User,
} from '@yingai/shared';
import type { CreateOrderResult, MyUsageView } from './types';

export interface LoginResult {
  tokens: Tokens;
  user: User;
}
export interface RegisterResult {
  tokens: Tokens;
  user: User;
}
export interface SendCodeResult {
  sent: boolean;
  debugCode?: string | null;
}

export const authApi = {
  sendCode: (target: string, purpose: 'register' | 'login') =>
    client.post('/auth/send-code', { target, purpose }) as unknown as Promise<SendCodeResult>,
  register: (body: RegisterRequest) =>
    client.post('/auth/register', body) as unknown as Promise<RegisterResult>,
  login: (body: LoginRequest) => client.post('/auth/login', body) as unknown as Promise<LoginResult>,
  logout: () => client.post('/auth/logout') as unknown as Promise<{ success: boolean }>,
};

export const agentsApi = {
  list: (params?: AgentListQuery) =>
    client.get('/agents', { params }) as unknown as Promise<{
      items: AgentSummary[];
      total: number;
      page: number;
      pageSize: number;
    }>,
  categories: () => client.get('/agents/categories') as unknown as Promise<AgentCategory[]>,
  match: (identity: string) =>
    client.get('/agents/match', { params: { identity } }) as unknown as Promise<{
      identity: string | null;
      items: AgentSummary[];
    }>,
  detail: (agentId: string) => client.get(`/agents/${agentId}`) as unknown as Promise<Agent>,
  access: (agentId: string) => client.get(`/agents/${agentId}/access`) as unknown as Promise<AgentAccess>,
};

export const planApi = {
  list: () => client.get('/plans') as unknown as Promise<Plan[]>,
};

export const ordersApi = {
  create: (body: { planCode: string; period: 'month' | 'year' }) =>
    client.post('/orders', body) as unknown as Promise<CreateOrderResult>,
  listMine: () => client.get('/orders/me') as unknown as Promise<Order[]>,
  detail: (id: string) => client.get(`/orders/${id}`) as unknown as Promise<Order & { payment: PrepayResult }>,
};

export const paymentApi = {
  mockSuccess: (paymentNo: string) =>
    client.post(`/payments/${paymentNo}/mock-success`) as unknown as Promise<{
      orderNo: string;
      status: string;
      planCode: string;
    }>,
};

export const userApi = {
  me: () => client.get('/users/me') as unknown as Promise<User>,
  update: (body: { nickname?: string; avatar?: string; identityType?: string }) =>
    client.patch('/users/me', body) as unknown as Promise<User>,
  quota: () => client.get('/users/me/quota') as unknown as Promise<QuotaInfo>,
  balance: () => client.get('/users/me/balance') as unknown as Promise<{ balance: number }>,
  usage: () => client.get('/users/me/usage') as unknown as Promise<MyUsageView>,
};

export const referralApi = {
  bind: (inviteCode: string) => client.post('/referral/bind', { inviteCode }) as unknown as Promise<unknown>,
  info: () => client.get('/referral/info') as unknown as Promise<ReferralInfo>,
  records: () => client.get('/referral/records') as unknown as Promise<ReferralRecord[]>,
};

export const tasksApi = {
  create: (agentId: string, body: CreateTaskRequest) =>
    client.post(`/agents/${encodeURIComponent(agentId)}/tasks`, body) as unknown as Promise<CreateTaskResult>,
  get: (taskId: string) => client.get(`/tasks/${taskId}`) as unknown as Promise<TaskView>,
};

export const conversationsApi = {
  list: (params?: ConversationListQuery) =>
    client.get('/conversations', { params }) as unknown as Promise<ConversationListResult>,
  detail: (id: string) => client.get(`/conversations/${id}`) as unknown as Promise<ConversationDetail>,
  rename: (id: string, title: string) =>
    client.patch(`/conversations/${id}`, { title }) as unknown as Promise<{ id: string; title: string }>,
  togglePin: (id: string) =>
    client.patch(`/conversations/${id}/pin`) as unknown as Promise<{ id: string; pinned: boolean }>,
  remove: (id: string) => client.delete(`/conversations/${id}`) as unknown as Promise<{ success: boolean }>,
};
